import { useAuthStore } from './store';

// Use environment variable for API URL (empty string for dev proxy, full URL for production)
const API_BASE = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api` : '/api';

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: unknown;
  headers?: Record<string, string>;
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;
  const token = useAuthStore.getState().token;

  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...headers,
    },
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(`${API_BASE}${endpoint}`, config);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      response.status,
      (errorData as { message?: string }).message || 'An error occurred'
    );
  }

  return response.json();
}

// Task types
export type TaskStatus = 'todo' | 'in_progress' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface TaskTag {
  name: string;
  color: string;
}

export interface TaskComment {
  id: string;
  text: string;
  userId: string;
  userName: string;
  createdAt: string;
}

export interface Task {
  id: string;
  title: string;
  description?: string;
  status: TaskStatus;
  priority: TaskPriority;
  dueDate?: string;
  assigneeId?: string;
  assigneeName?: string;
  documentId?: string;
  parentTaskId?: string;
  position: number;
  timeEstimate?: number;
  timeTracked?: number;
  recurring?: 'daily' | 'weekly' | 'biweekly' | 'monthly';
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  subtaskCount?: number;
  completedSubtaskCount?: number;
  tags?: TaskTag[];
  subtasks?: Task[];
  comments?: TaskComment[];
}

export interface TaskStats {
  total: number;
  todo: number;
  inProgress: number;
  done: number;
  urgent: number;
  overdue: number;
  completedThisWeek: number;
}

// Document types
export interface Document {
  id: string;
  title: string;
  content?: string;
  icon?: string;
  coverImage?: string;
  parentId: string | null;
  ownerId: string;
  isArchived: boolean;
  isFavorited: boolean;
  createdAt: string;
  updatedAt: string;
  permissionLevel?: 'owner' | 'editor' | 'viewer';
}

export interface DocumentVersion {
  id: string;
  versionNumber: number;
  createdBy: string;
  createdByName: string;
  createdAt: string;
}

export interface User {
  id: string;
  email: string;
  name: string;
  avatarUrl?: string;
  preferredLanguage?: 'en' | 'ar';
  createdAt?: string;
}

// Document API
export const documentsApi = {
  list: () => api<{ documents: Document[] }>('/documents'),
  get: (id: string) => api<{ document: Document }>(`/documents/${id}`),
  create: (data: { title?: string; parentId?: string; icon?: string }) =>
    api<{ document: Document }>('/documents', { method: 'POST', body: data }),
  update: (id: string, data: Partial<Document>) =>
    api<{ document: Document }>(`/documents/${id}`, { method: 'PATCH', body: data }),
  delete: (id: string) =>
    api<{ success: boolean }>(`/documents/${id}`, { method: 'DELETE' }),
  share: (id: string, email: string, permissionLevel: 'editor' | 'viewer') =>
    api<{ success: boolean }>(`/documents/${id}/share`, { method: 'POST', body: { email, permissionLevel } }),
  getVersions: (id: string) =>
    api<{ versions: DocumentVersion[] }>(`/documents/${id}/versions`),
  restoreVersion: (documentId: string, versionId: string) =>
    api<{ success: boolean }>(`/documents/${documentId}/versions/${versionId}/restore`, { method: 'POST' }),
};

// Auth API
export const authApi = {
  login: (email: string, password: string) =>
    api<{ user: User; token: string }>('/auth/login', { method: 'POST', body: { email, password } }),
  signup: (email: string, password: string, name: string, preferredLanguage?: 'en' | 'ar') =>
    api<{ user: User; token: string }>('/auth/signup', { method: 'POST', body: { email, password, name, preferredLanguage } }),
  me: () => api<{ user: User }>('/auth/me'),
  updateProfile: (data: { name?: string; avatarUrl?: string; preferredLanguage?: 'en' | 'ar' }) =>
    api<{ success: boolean }>('/auth/me', { method: 'PATCH', body: data }),
  logout: () => api<{ success: boolean }>('/auth/logout', { method: 'POST' }),
};

// File Upload API
export const filesApi = {
  upload: async (file: File, documentId?: string, type?: 'attachment' | 'export') => {
    const formData = new FormData();
    formData.append('file', file);
    if (documentId) formData.append('documentId', documentId);
    if (type) formData.append('type', type);
    
    const token = useAuthStore.getState().token;
    const response = await fetch(`${API_BASE}/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: formData,
    });
    if (!response.ok) throw new ApiError(response.status, 'File upload failed');
    return response.json() as Promise<{ url: string; key: string; name: string; size: number }>;
  },
  list: (documentId: string) =>
    api<{ files: { key: string; size: number; uploaded: string; url: string }[] }>(`/files/list/${documentId}`),
  delete: (bucket: 'attachments' | 'exports', key: string) =>
    api<{ success: boolean }>(`/files/${bucket}/${key}`, { method: 'DELETE' }),
};

// Task API
export interface TaskFilters {
  status?: TaskStatus;
  priority?: TaskPriority;
  assigneeId?: string;
  documentId?: string;
  dueBefore?: string;
  dueAfter?: string;
  search?: string;
  sortBy?: 'created_at' | 'updated_at' | 'due_date' | 'priority' | 'position' | 'title';
  sortOrder?: 'asc' | 'desc';
  parentOnly?: boolean;
}

export const tasksApi = {
  list: (filters?: TaskFilters) => {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined) params.append(key, String(value));
      });
    }
    return api<{ tasks: Task[] }>(`/tasks?${params.toString()}`);
  },
  
  get: (id: string) => api<{ task: Task }>(`/tasks/${id}`),
  
  create: (data: {
    title: string;
    description?: string;
    status?: TaskStatus;
    priority?: TaskPriority;
    dueDate?: string;
    assigneeId?: string;
    documentId?: string;
    parentTaskId?: string;
    tags?: { name: string; color?: string }[];
  }) => api<{ task: Task }>('/tasks', { method: 'POST', body: data }),
  
  update: (id: string, data: Partial<Task>) =>
    api<{ task: Task }>(`/tasks/${id}`, { method: 'PATCH', body: data }),
  
  delete: (id: string) => api<{ success: boolean }>(`/tasks/${id}`, { method: 'DELETE' }),
  
  reorder: (tasks: { id: string; position: number; status?: TaskStatus }[]) =>
    api<{ success: boolean }>('/tasks/reorder', { method: 'POST', body: { tasks } }),
  
  addComment: (taskId: string, text: string) =>
    api<{ comment: TaskComment }>(`/tasks/${taskId}/comments`, { method: 'POST', body: { text } }),
  
  addTag: (taskId: string, name: string, color?: string) =>
    api<{ success: boolean }>(`/tasks/${taskId}/tags`, { method: 'POST', body: { name, color } }),
  
  removeTag: (taskId: string, tagName: string) =>
    api<{ success: boolean }>(`/tasks/${taskId}/tags/${tagName}`, { method: 'DELETE' }),
  
  getStats: () => api<{ stats: TaskStats }>('/tasks/stats/summary'),
};

// AI API
export type WriteAction = 'continue' | 'improve' | 'fix' | 'shorter' | 'longer' | 'professional' | 'casual' | 'friendly';
export type SummarizeStyle = 'tldr' | 'bullets' | 'paragraph';

export const aiApi = {
  write: (text: string, action: WriteAction, language?: 'en' | 'ar') =>
    api<{ result: string; action: WriteAction; language: string }>('/ai/write', { method: 'POST', body: { text, action, language } }),
  
  writeStream: async (text: string, action: WriteAction, language?: 'en' | 'ar') => {
    const token = useAuthStore.getState().token;
    const response = await fetch(`${API_BASE}/ai/write/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ text, action, language }),
    });
    if (!response.ok) throw new ApiError(response.status, 'Stream failed');
    return response;
  },
  
  translate: (text: string, from: 'en' | 'ar' | 'auto', to: 'en' | 'ar') =>
    api<{ translation: string; cached?: boolean }>('/ai/translate', { method: 'POST', body: { text, from, to } }),
  
  summarize: (text: string, style?: SummarizeStyle, language?: 'en' | 'ar') =>
    api<{ summary: string }>('/ai/summarize', { method: 'POST', body: { text, style, language } }),
  
  ask: (question: string, context: string, language?: 'en' | 'ar') =>
    api<{ answer: string }>('/ai/ask', { method: 'POST', body: { question, context, language } }),
  
  extractActionItems: (text: string, language?: 'en' | 'ar') =>
    api<{ actionItems: string }>('/ai/action-items', { method: 'POST', body: { text, language } }),
  
  generateToc: (text: string, language?: 'en' | 'ar') =>
    api<{ toc: string }>('/ai/toc', { method: 'POST', body: { text, language } }),
  
  complete: (prompt: string, context?: string) => {
    const params = new URLSearchParams({ prompt });
    if (context) params.append('context', context);
    return api<{ completion: string }>(`/ai/complete?${params.toString()}`);
  },
};

export { api, ApiError };
